import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.tsx';
import { MemoryRouter } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ChakraProvider } from '@chakra-ui/react';
import theme from '@theme/index.ts';
import { UserProvider } from '@providers/useUserContext.tsx';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { Fonts } from 'theme/fonts/index.tsx';
import { primeMarketingConsent } from './utils/consent.ts';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { HashRouter } from 'react-router-dom';
import './i18n.ts';
import './index.css';

import * as Sentry from '@sentry/react';
import { safeLocalStorage } from 'utils/safeStorage';
import { isUxV2 } from './v2/flag.ts';
import { captureHandoffFromUrl } from './v2/lib/handoffCapture.ts';
import { IGNORE_ERRORS, isBusinessApiError } from './utils/sentryFilters.ts';

// ============================================================================
// INTERCEPTION DU RETOUR ALMA — DOIT TOURNER AVANT LE MONTAGE DU HASHROUTER
// ============================================================================
// Alma renvoie le client sur `/reservation/?pid=xxx` : la query est AVANT le
// hash, donc invisible pour le HashRouter (qui ne lit que `#/...`). L'ancien
// pont (custom-menu.js : replaceState à load+500ms) perdait la course contre
// le montage de React — replaceState ne déclenche aucun événement router, le
// pid était détruit silencieusement et le client jamais crédité (incident
// Dynamo Marseille du 27/05/2026). Ici on réécrit l'URL de façon SYNCHRONE
// avant que le router ne lise le hash : plus de course possible.
const interceptedAlmaPid = (function interceptAlmaReturn(): string | null {
  const pid = new URLSearchParams(window.location.search).get('pid');
  if (!pid) return null;

  // Filet de sécurité lu par App.tsx si l'URL est perdue malgré tout.
  // safeLocalStorage : un storage indisponible ne doit JAMAIS faire planter
  // l'interception (le pid resterait dans l'URL réécrite de toute façon).
  safeLocalStorage.setItem('alma_pending_payment', pid);
  safeLocalStorage.setItem('alma_pending_timestamp', Date.now().toString());

  // Déplacer le pid dans le fragment pour que le HashRouter le voie au mount
  window.history.replaceState(
    null,
    '',
    `${window.location.origin}${window.location.pathname}#/checkout/?pid=${pid}`
  );

  return pid;
})();

// Même histoire pour le retour d'un paiement par portefeuille, à un détail
// près qui change tout : Mindbody rend le `paymentAccessToken` sur l'adresse
// que nous lui avons donnée, et ce jeton suffit à finaliser la commande. Le
// laisser dans la query string aurait deux conséquences. D'abord une boucle :
// le HashRouter ne lit que le fragment, une navigation ne nettoie pas la
// query, et la route de retour se remonterait à chaque changement de page, en
// rappelant la finalisation. Ensuite une fuite : l'URL complète part dans les
// events et les replays Sentry, dans GA4 et dans l'historique du navigateur.
// On réécrit donc l'adresse de façon SYNCHRONE, avant l'init de Sentry.
(function interceptWalletReturn(): void {
  const token = new URLSearchParams(window.location.search).get('paymentAccessToken');
  if (!token) return;

  window.history.replaceState(
    null,
    '',
    `${window.location.origin}${window.location.pathname}#/wallet-callback?paymentAccessToken=${encodeURIComponent(token)}`,
  );
})();

// Nouvelle interface (v2) : connexion venue du site d'une autre marque de la ville (docs/ux-v2/HANDOFF.md). Le
// jeton (`mm_handoff`) et le marqueur (`mm_sso`) quittent le fragment AVANT l'init de Sentry, pour la même raison que
// le `paymentAccessToken` ci-dessus ; mountV2 les reprend en mémoire. Flag éteint : rien ne change.
if (isUxV2()) captureHandoffFromUrl();

Sentry.init({
  // Sentry cloud, organisation monday-zf (région DE), projet monday-mindbody-front.
  dsn: 'https://ebaaebaa740c976e63d41a0a1c99a4ad@o4512225145913344.ingest.de.sentry.io/4512225150566480',
  // Release = sha du commit injecté au build (vite.config.ts, `define`).
  // OBLIGATOIRE pour la symbolication : c'est ce qui associe les events aux
  // sourcemaps uploadées par le plugin Vite. Sans release, stacks minifiées.
  release: __SENTRY_RELEASE__,
  // Pas de captureConsoleIntegration : elle captait la console de TOUTE la page
  // WordPress (GTM, Meta Pixel, Cookiebot, extensions navigateur), d'où la liste
  // noire de 70 motifs qui vivait ici. Les signaux du plugin passent par des
  // appels Sentry explicites. Pas de browserTracingIntegration non plus : une
  // transaction par ouverture de page et par navigation sur 10 sites, pour un
  // onglet Performance que personne ne consulte, c'est le quota de spans.
  integrations: [],
  tracesSampleRate: 0,

  // Replays : aucun. Relevé du 12/10/2026 : à 0.1 sur erreur, 84 replays acceptés
  // en trois jours pour un quota mensuel de 50 (plan Developer). L'intégration
  // n'est plus chargée, ce qui évite aussi l'enregistrement DOM en mémoire ;
  // pour réactiver, remettre Sentry.replayIntegration() et un taux non nul.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,

  // Erreurs de scripts tiers remontées par window.onerror depuis la page hôte
  // (la frame fautive est le script inline de /reservation/, hors de portée de
  // denyUrls). Liste et justification dans utils/sentryFilters.ts.
  ignoreErrors: IGNORE_ERRORS,

  // Scripts tiers chargés par les sites : leurs exceptions ne sont pas les
  // nôtres et ne sont pas corrigeables ici.
  denyUrls: [
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-(web-)?extension:\/\//i,
    /googletagmanager\.com/i,
    /google-analytics\.com/i,
    /connect\.facebook\.net/i,
    /consent\.cookiebot\.com/i,
    /consentcdn\.cookiebot\.com/i,
  ],

  beforeSend(event, hint) {
    // Refus métier 4xx de notre API : breadcrumb pour l'erreur suivante, pas d'event.
    if (isBusinessApiError(event, hint)) {
      const e = hint.originalException as { Code: number; ErrorCode: string; Message: string };
      Sentry.addBreadcrumb({
        category: 'api',
        level: 'warning',
        message: `API ${e.Code} ${e.ErrorCode}: ${e.Message}`,
      });
      return null;
    }

    // Garder le filtre existant pour les erreurs CSS et NotFoundError
    if (event.exception && event.exception.values) {
      const errorMessage = event.exception.values[0].value;
      const errorName = event.exception.values[0].type;
      if (
        (errorMessage && errorMessage.includes('Unable to preload CSS')) ||
        (errorName === 'NotFoundError' &&
          (errorMessage?.includes('The object can not be found here') ||
            errorMessage?.includes("Failed to execute 'removeChild' on 'Node'"))) ||
        (errorMessage && errorMessage.includes('cybotCookiebotDialogBodyButtonAccept'))
      ) {
        return null;
      }
    }
    return event;
  },
});

// Signal d'observabilité du retour Alma : émis dès que Sentry est prêt.
// C'est l'event qui manquait pour voir l'incident du 27/05/2026 (le pid
// arrivait dans l'URL puis disparaissait sans aucune trace).
if (interceptedAlmaPid) {
  Sentry.captureMessage('Alma return intercepted', {
    level: 'info',
    tags: {
      category: 'payment',
      paymentMethod: 'alma',
      alma_step: 'return_intercepted',
    },
    extra: {
      paymentId: interceptedAlmaPid,
    },
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000,
    },
  },
});

persistQueryClient({
  queryClient,
  // L'accès même à `window.localStorage` peut throw (SecurityError) ou
  // renvoyer null (Firefox dom.storage désactivé) : dans ce cas on désactive
  // simplement la persistance du cache react-query au lieu de crasher au boot.
  persister: createSyncStoragePersister({
    storage: (() => {
      try {
        return window.localStorage ?? undefined;
      } catch {
        return undefined;
      }
    })(),
  }),
});

// Abonnement au bandeau de cookies TCF (API asynchrone) pour que le
// consentement soit déjà connu au moment de valider un paiement.
primeMarketingConsent();

const element = document.getElementById('mindbody-app');

function mountV1(container: HTMLElement) {
  const root = ReactDOM.createRoot(container);
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <ChakraProvider theme={theme}>
          <Fonts />
          <UserProvider>
            <HashRouter>
              <App />
            </HashRouter>
          </UserProvider>
        </ChakraProvider>
      </QueryClientProvider>
    </React.StrictMode>,
  );
}

// Aiguillage unique de la nouvelle interface (docs/ux-v2/SPEC.md § 6.2) : `mondayMindbody.uxV2`
// n'est injecté que si la v2 est active sur le site ; sinon, montage v1 ci-dessous, inchangé.
// La v2 est un morceau à part (dist/v2/, hors du dossier enqueue par Frontend.php, CSS compris),
// téléchargé ici seulement : flag éteint, aucun octet de la v2 n'est chargé (SPEC § 6.2, règle 3).
if (element && isUxV2()) {
  import('./v2/mount.tsx').then(
    ({ mountV2 }) => mountV2(ReactDOM.createRoot(element), element, queryClient),
    (error) => {
      // Morceau introuvable (réseau, déploiement en cours) : l'app actuelle plutôt qu'une page vide.
      Sentry.captureException(error, { tags: { ux: 'v2', ux_v2_load: 'failed' } });
      mountV1(element);
    },
  );
} else if (element) {
  mountV1(element);
}

class MondayMindbodyApp extends HTMLElement {
  connectedCallback() {
    const mountPoint = document.createElement('body');
    this.attachShadow({ mode: 'open' }).appendChild(mountPoint);

    const cache = createCache({
      key: 'monday-mindbody',
      container: mountPoint,
    });

    const root = ReactDOM.createRoot(mountPoint);

    root.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <CacheProvider value={cache}>
            <ChakraProvider theme={theme}>
              <Fonts />
              <UserProvider>
                <MemoryRouter>
                  <App />
                </MemoryRouter>
              </UserProvider>
            </ChakraProvider>
          </CacheProvider>
        </QueryClientProvider>
      </React.StrictMode>,
    );
  }
}

customElements.define('monday-mindbody-app', MondayMindbodyApp);
